import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Wallet, CalendarHeart, BookOpen, ArrowUpRight, TrendingUp, TrendingDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCollection, usePrefs } from "@/hooks/useCollection";
import { rupiah, monthKey, sum, fmtDate } from "@/lib/format";
import { predictions } from "@/lib/period";
import { MascotBubble } from "@/components/Mascot";
import { differenceInCalendarDays, parseISO } from "date-fns";

const Tile = ({ to, icon: Icon, title, children, tone, testid, delay }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}>
    <Link to={to} data-testid={testid} className="card-soft block group h-full">
      <div className="flex items-center justify-between mb-3">
        <span className={`w-10 h-10 rounded-2xl grid place-items-center ${tone}`}><Icon className="w-5 h-5" /></span>
        <ArrowUpRight className="w-5 h-5 text-pink-300 group-hover:text-pink-500 transition-colors" />
      </div>
      <p className="font-heading text-lg font-semibold text-gray-800">{title}</p>
      <div className="text-sm text-gray-500 mt-1">{children}</div>
    </Link>
  </motion.div>
);

export default function HomePage() {
  const { user } = useAuth();
  const m = monthKey();
  const { items: inc } = useCollection("incomes", m);
  const { items: exp } = useCollection("expenses", m);
  const { items: periods } = useCollection("periods");
  const { prefs } = usePrefs();
  const next = predictions(periods, prefs, 1)[0];
  const daysTo = next ? differenceInCalendarDays(parseISO(next.start), new Date()) : null;
  const tIn = sum(inc), tOut = sum(exp);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-pink-500 font-medium">Selamat datang kembali</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-gray-800" data-testid="home-greeting">Hai, {user.name}!</h1>
      </div>
      <MascotBubble pose="wave" testid="home-mascot-tip">
        {next ? (daysTo > 0 ? `Mens berikutnya diprediksi ${daysTo} hari lagi (${fmtDate(next.start)}). Siapkan pembalut & cemilan ya!` : "Lagi masa mens ya? Jangan lupa minum air hangat dan istirahat cukup.") : "Catat tanggal mens pertamamu di Mood Aku, nanti aku bantu prediksi berikutnya."}
      </MascotBubble>
      <div className="card-soft bg-gradient-to-br from-pink-400 to-rose-400 !border-0 text-white" data-testid="home-balance-card">
        <p className="text-sm opacity-90">Sisa uangku bulan ini</p>
        <p className="font-heading text-4xl font-semibold mt-1">{rupiah(tIn - tOut)}</p>
        <div className="flex gap-6 mt-4 text-sm">
          <span className="flex items-center gap-1.5"><TrendingUp className="w-4 h-4" /> {rupiah(tIn)}</span>
          <span className="flex items-center gap-1.5"><TrendingDown className="w-4 h-4" /> {rupiah(tOut)}</span>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Tile to="/duitku" icon={Wallet} title="Duitku" tone="bg-pink-100 text-pink-500" testid="home-tile-duitku" delay={0.05}>Gajiku, komisiku, tipsku, tabunganku & saran Baby Monster</Tile>
        <Tile to="/mood" icon={CalendarHeart} title="Mood Aku" tone="bg-rose-100 text-rose-500" testid="home-tile-mood" delay={0.1}>Kalender mens, jadwal kerja & acara seru</Tile>
        <Tile to="/baca" icon={BookOpen} title="Baca Yuk" tone="bg-sky-100 text-sky-500" testid="home-tile-baca" delay={0.15}>Berita terbaru dari topik favoritku</Tile>
      </div>
    </div>
  );
}
